<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from '@/i18n'

const { t } = useI18n()

interface Scene {
  id?: number
  idx: number
  start_time: number
  duration_s: number
  description: string | null
  image_prompt: string | null
  video_prompt: string | null
  status?: string
}

const props = defineProps<{
  projectId: number
  scenes: Scene[]
  projectStatus: string
  projectMode: string
}>()

const emit = defineEmits<{
  (e: 'validate'): void
  (e: 'update-scene', sceneId: number, data: Partial<Scene>): void
  (e: 'create-scene', data: Scene): void
  (e: 'delete-scene', sceneId: number): void
  (e: 'refresh'): void
}>()

const editingScene = ref<Scene | null>(null)
const showEditDialog = ref(false)
const showAddDialog = ref(false)
const newScene = ref<Scene>({
  idx: 0,
  start_time: 0,
  duration_s: 10,
  description: '',
  image_prompt: '',
  video_prompt: ''
})
const saving = ref(false)

const canEdit = computed(() => 
  props.projectStatus === 'script_ready' && props.projectMode === 'manual'
)

const totalDuration = computed(() => 
  props.scenes.reduce((sum, s) => sum + s.duration_s, 0)
)

function editScene(scene: Scene) {
  editingScene.value = { ...scene }
  showEditDialog.value = true
}

function openAddDialog() {
  // Calculate start time as end of last scene
  const lastScene = props.scenes[props.scenes.length - 1]
  const startTime = lastScene ? lastScene.start_time + lastScene.duration_s : 0
  
  newScene.value = {
    idx: props.scenes.length,
    start_time: startTime,
    duration_s: 10,
    description: '',
    image_prompt: '',
    video_prompt: ''
  }
  showAddDialog.value = true
}

async function saveScene() {
  if (!editingScene.value || !editingScene.value.id) return
  
  saving.value = true
  try {
    emit('update-scene', editingScene.value.id, {
      description: editingScene.value.description,
      image_prompt: editingScene.value.image_prompt,
      video_prompt: editingScene.value.video_prompt,
      duration_s: editingScene.value.duration_s
    })
    showEditDialog.value = false
    ElMessage.success(t('script.scene_updated'))
  } catch (error) {
    ElMessage.error(t('script.error_update'))
  } finally {
    saving.value = false
  }
}

async function addScene() {
  if (!newScene.value.description || !newScene.value.image_prompt || !newScene.value.video_prompt) {
    ElMessage.warning(t('script.fill_all_fields'))
    return
  }
  
  saving.value = true
  try {
    emit('create-scene', newScene.value)
    showAddDialog.value = false
    ElMessage.success(t('script.scene_added'))
  } catch (error) {
    ElMessage.error(t('script.error_create'))
  } finally {
    saving.value = false
  }
}

async function confirmDelete(scene: Scene) {
  if (!scene.id) return
  
  try {
    await ElMessageBox.confirm(
      t('script.confirm_delete_message'),
      t('script.confirm_delete_title'),
      {
        confirmButtonText: t('common.delete'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    emit('delete-scene', scene.id)
    ElMessage.success(t('script.scene_deleted'))
  } catch {
    // Cancelled
  }
}

async function validateScript() {
  if (props.scenes.length === 0) {
    ElMessage.warning(t('script.no_scenes'))
    return
  }
  
  try {
    await ElMessageBox.confirm(
      t('script.confirm_validate_message'),
      t('script.confirm_validate_title'),
      {
        confirmButtonText: t('script.validate'),
        cancelButtonText: t('common.cancel'),
        type: 'info'
      }
    )
    emit('validate')
  } catch {
    // Cancelled
  }
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
}
</script>

<template>
  <div class="script-editor">
    <!-- Header -->
    <div class="editor-header">
      <div class="header-info">
        <h2>{{ t('script.title') }}</h2>
        <div class="header-stats">
          <span class="stat">
            <strong>{{ scenes.length }}</strong> {{ t('script.scenes') }}
          </span>
          <span class="stat">
            <strong>{{ formatDuration(totalDuration) }}</strong> {{ t('script.total_duration') }}
          </span>
        </div>
      </div>
      
      <div v-if="canEdit" class="header-actions">
        <el-button @click="openAddDialog" type="primary" plain>
          <span class="btn-icon">+</span>
          {{ t('script.add_scene') }}
        </el-button>
        <el-button @click="validateScript" type="success">
          {{ t('script.validate_and_generate') }}
        </el-button>
      </div>
    </div>

    <!-- Scenes List -->
    <div class="scenes-list">
      <div 
        v-for="scene in scenes" 
        :key="scene.id || scene.idx"
        class="scene-item"
      >
        <div class="scene-number">
          <span>{{ scene.idx + 1 }}</span>
        </div>
        
        <div class="scene-content">
          <div class="scene-meta">
            <span class="scene-time">{{ formatDuration(scene.start_time) }} - {{ formatDuration(scene.start_time + scene.duration_s) }}</span>
            <span class="scene-duration">{{ scene.duration_s.toFixed(1) }}s</span>
          </div>
          
          <div class="scene-description">
            <strong>{{ t('script.description') }}:</strong>
            <p>{{ scene.description || '-' }}</p>
          </div>
          
          <div class="scene-prompts">
            <div class="prompt-item">
              <span class="prompt-label">{{ t('script.image_prompt') }}:</span>
              <p class="prompt-text">{{ scene.image_prompt || '-' }}</p>
            </div>
            <div class="prompt-item">
              <span class="prompt-label">{{ t('script.video_prompt') }}:</span>
              <p class="prompt-text">{{ scene.video_prompt || '-' }}</p>
            </div>
          </div>
        </div>
        
        <div v-if="canEdit" class="scene-actions">
          <el-button @click="editScene(scene)" type="primary" text>
            {{ t('common.edit') }}
          </el-button>
          <el-button @click="confirmDelete(scene)" type="danger" text>
            {{ t('common.delete') }}
          </el-button>
        </div>
      </div>
      
      <div v-if="scenes.length === 0" class="empty-state">
        <p>{{ t('script.no_scenes') }}</p>
      </div>
    </div>

    <!-- Edit Scene Dialog -->
    <el-dialog
      v-model="showEditDialog"
      :title="t('script.edit_scene')"
      width="700px"
      destroy-on-close
    >
      <el-form v-if="editingScene" label-position="top" class="scene-form">
        <el-form-item :label="t('script.duration_s')">
          <el-input-number 
            v-model="editingScene.duration_s" 
            :min="1" 
            :max="30"
            :step="0.5"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.description')">
          <el-input 
            v-model="editingScene.description" 
            type="textarea"
            :rows="2"
            :placeholder="t('script.description_placeholder')"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.image_prompt')">
          <el-input 
            v-model="editingScene.image_prompt" 
            type="textarea"
            :rows="3"
            :placeholder="t('script.image_prompt_placeholder')"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.video_prompt')">
          <el-input 
            v-model="editingScene.video_prompt" 
            type="textarea"
            :rows="3"
            :placeholder="t('script.video_prompt_placeholder')"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button @click="showEditDialog = false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" @click="saveScene" :loading="saving">
          {{ t('common.save') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- Add Scene Dialog -->
    <el-dialog
      v-model="showAddDialog"
      :title="t('script.add_scene')"
      width="700px"
      destroy-on-close
    >
      <el-form label-position="top" class="scene-form">
        <el-form-item :label="t('script.duration_s')">
          <el-input-number 
            v-model="newScene.duration_s" 
            :min="1" 
            :max="30"
            :step="0.5"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.description')" required>
          <el-input 
            v-model="newScene.description" 
            type="textarea"
            :rows="2"
            :placeholder="t('script.description_placeholder')"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.image_prompt')" required>
          <el-input 
            v-model="newScene.image_prompt" 
            type="textarea"
            :rows="3"
            :placeholder="t('script.image_prompt_placeholder')"
          />
        </el-form-item>
        
        <el-form-item :label="t('script.video_prompt')" required>
          <el-input 
            v-model="newScene.video_prompt" 
            type="textarea"
            :rows="3"
            :placeholder="t('script.video_prompt_placeholder')"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button @click="showAddDialog = false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" @click="addScene" :loading="saving">
          {{ t('script.add') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.script-editor {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--m2v-space-6);
  padding-bottom: var(--m2v-space-4);
  border-bottom: 1px solid var(--m2v-border-subtle);
}

.editor-header h2 {
  margin: 0 0 var(--m2v-space-2) 0;
  font-size: 1.5rem;
}

.header-stats {
  display: flex;
  gap: var(--m2v-space-4);
  color: var(--m2v-text-muted);
}

.stat strong {
  color: var(--m2v-text-primary);
}

.header-actions {
  display: flex;
  gap: var(--m2v-space-3);
}

.btn-icon {
  font-size: 1.25rem;
  margin-right: var(--m2v-space-1);
}

.scenes-list {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-4);
}

.scene-item {
  display: flex;
  gap: var(--m2v-space-4);
  padding: var(--m2v-space-4);
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-fast);
}

.scene-item:hover {
  border-color: var(--m2v-primary);
}

.scene-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
  border-radius: var(--m2v-radius-lg);
}

.scene-content {
  flex: 1;
  min-width: 0;
}

.scene-meta {
  display: flex;
  gap: var(--m2v-space-4);
  margin-bottom: var(--m2v-space-2);
  font-size: 0.875rem;
}

.scene-time {
  color: var(--m2v-text-muted);
}

.scene-duration {
  background: var(--m2v-bg-surface);
  padding: 2px 8px;
  border-radius: var(--m2v-radius-sm);
  font-weight: 600;
}

.scene-description {
  margin-bottom: var(--m2v-space-3);
}

.scene-description strong {
  color: var(--m2v-text-secondary);
  font-size: 0.75rem;
  text-transform: uppercase;
}

.scene-description p {
  margin: var(--m2v-space-1) 0 0;
  color: var(--m2v-text-primary);
}

.scene-prompts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--m2v-space-3);
}

.prompt-item {
  background: var(--m2v-bg-surface);
  padding: var(--m2v-space-3);
  border-radius: var(--m2v-radius-md);
}

.prompt-label {
  display: block;
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  text-transform: uppercase;
  margin-bottom: var(--m2v-space-1);
}

.prompt-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--m2v-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.scene-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-2);
}

.empty-state {
  text-align: center;
  padding: var(--m2v-space-12);
  color: var(--m2v-text-muted);
}

.scene-form :deep(.el-form-item__label) {
  font-weight: 600;
}

@media (max-width: 768px) {
  .editor-header {
    flex-direction: column;
    gap: var(--m2v-space-4);
  }
  
  .scene-item {
    flex-direction: column;
  }
  
  .scene-number {
    width: 100%;
    height: 36px;
  }
  
  .scene-prompts {
    grid-template-columns: 1fr;
  }
  
  .scene-actions {
    flex-direction: row;
  }
}
</style>
